import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useNavigate } from "react-router-dom";
import { track } from "@/lib/api";
import { Calculator, ArrowRight } from "lucide-react";

const WEEKS_PER_MONTH = 4.33;

function money(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Math.round(n || 0));
}
function money2(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n || 0);
}
function hoursFmt(n) {
  return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(n || 0)} hrs`;
}
function decisionFromNet(net, cost) {
  if (net > cost * 2) return "Expand";
  if (net > 0) return "Continue";
  return "Review";
}

/**
 * Config-driven deterministic calculators. All math runs client-side on the
 * visitor's own inputs — nothing is estimated for them.
 */
const CALCULATORS = {
  ai_roi: {
    title: "AI ROI Calculator",
    fields: [
      ["team", "People using AI", 10],
      ["hoursPerWeek", "Hours saved / person / week", 4],
      ["hourlyCost", "Average loaded hourly cost ($)", 60],
      ["toolCost", "AI tool cost / month ($)", 600],
    ],
    compute: (v) => {
      const monthlyHours = v.team * v.hoursPerWeek * WEEKS_PER_MONTH;
      const labour = monthlyHours * v.hourlyCost;
      const net = labour - v.toolCost;
      const payback = labour > 0 ? Math.max(0, (v.toolCost / labour) * 30) : null;
      return {
        metrics: [
          ["Time saved", hoursFmt(monthlyHours)], ["Labour value", money(labour)],
          ["AI cost", money(v.toolCost)], ["Net value", money(net), net >= 0],
        ],
        extra: [["Payback", payback == null ? "—" : payback < 1 ? "Same day" : `${Math.ceil(payback)} days`]],
        decision: decisionFromNet(net, v.toolCost),
      };
    },
  },
  time_savings: {
    title: "AI Time Savings Calculator",
    fields: [
      ["tasks", "Tasks per month", 100],
      ["without", "Minutes per task without AI", 45],
      ["withAi", "Minutes per task with AI", 15],
      ["people", "People doing this work", 5],
    ],
    compute: (v) => {
      const saved = (v.tasks * Math.max(0, v.without - v.withAi)) / 60;
      return {
        metrics: [
          ["Hours saved / month", hoursFmt(saved)], ["Per person / month", hoursFmt(v.people ? saved / v.people : 0)],
          ["Hours saved / year", hoursFmt(saved * 12)], ["Days released / year", `${((saved * 12) / 8).toFixed(0)} days`],
        ],
      };
    },
  },
  tco: {
    title: "AI Total Cost of Ownership Calculator",
    fields: [
      ["seats", "Seats", 100],
      ["price", "Price / seat / month ($)", 30],
      ["implementation", "One-time implementation ($)", 2400],
      ["trainingHours", "Training hours per seat", 1.5],
      ["adminHours", "Admin / governance hours per month", 2],
      ["rate", "Loaded hourly cost ($)", 75],
    ],
    compute: (v) => {
      const licences = v.seats * v.price * 12;
      const training = v.seats * v.trainingHours * v.rate;
      const admin = v.adminHours * v.rate * 12;
      const tco = licences + v.implementation + training + admin;
      return {
        metrics: [
          ["Licences / year", money(licences)], ["Setup + training", money(v.implementation + training)],
          ["Year-one TCO", money(tco), false], ["All-in / seat / month", money2(v.seats ? tco / v.seats / 12 : 0)],
        ],
      };
    },
  },
  break_even: {
    title: "AI Break-Even Calculator",
    fields: [
      ["cost", "Total AI cost / month ($)", 3000],
      ["rate", "Loaded hourly cost ($)", 75],
      ["saved", "Hours actually saved / month", 60],
    ],
    compute: (v) => {
      const needed = v.rate > 0 ? v.cost / v.rate : 0;
      const surplusH = v.saved - needed;
      const surplus = surplusH * v.rate;
      return {
        metrics: [
          ["Break-even hours / month", hoursFmt(needed)], ["Hours saved", hoursFmt(v.saved)],
          ["Surplus hours", hoursFmt(surplusH), surplusH >= 0], ["Net value / month", money(surplus), surplus >= 0],
        ],
        decision: decisionFromNet(surplus, v.cost),
      };
    },
  },
  review_cost: {
    title: "AI Human Review Cost Calculator",
    fields: [
      ["outputs", "AI outputs per month", 500],
      ["reviewMins", "Review minutes per output", 4],
      ["rate", "Reviewer loaded hourly cost ($)", 75],
    ],
    compute: (v) => {
      const hours = (v.outputs * v.reviewMins) / 60;
      const cost = hours * v.rate;
      return {
        metrics: [
          ["Review hours / month", hoursFmt(hours)], ["Review cost / month", money(cost), false],
          ["Cost per output", money2(v.outputs ? cost / v.outputs : 0)], ["Review cost / year", money(cost * 12), false],
        ],
      };
    },
  },
  rework_cost: {
    title: "AI Rework Cost Calculator",
    fields: [
      ["outputs", "AI outputs per month", 500],
      ["errorRate", "Outputs needing rework (%)", 10],
      ["reworkMins", "Rework minutes per failed output", 20],
      ["rate", "Loaded hourly cost ($)", 75],
    ],
    compute: (v) => {
      const failed = v.outputs * (v.errorRate / 100);
      const hours = (failed * v.reworkMins) / 60;
      const cost = hours * v.rate;
      return {
        metrics: [
          ["Outputs reworked / month", `${failed.toFixed(0)}`], ["Rework hours / month", hoursFmt(hours)],
          ["Rework cost / month", money(cost), false], ["Rework cost / year", money(cost * 12), false],
        ],
      };
    },
  },
  agent_roi: {
    title: "AI Agent ROI Calculator",
    fields: [
      ["interactions", "Interactions handled / month", 1000],
      ["acceptRate", "Accepted / successful (%)", 65],
      ["minsSaved", "Staff minutes saved per accepted outcome", 12],
      ["reviewMins", "Human review minutes per accepted outcome", 2],
      ["rate", "Loaded hourly cost ($)", 55],
      ["platformCost", "Agent platform cost / month ($)", 1500],
    ],
    compute: (v) => {
      const accepted = v.interactions * (v.acceptRate / 100);
      const netHours = (accepted * Math.max(0, v.minsSaved - v.reviewMins)) / 60;
      const labour = netHours * v.rate;
      const net = labour - v.platformCost;
      return {
        metrics: [
          ["Successful outcomes / month", `${accepted.toFixed(0)}`],
          ["Cost per successful outcome", money2(accepted ? v.platformCost / accepted : 0)],
          ["Net staff hours saved", hoursFmt(netHours)], ["Net value / month", money(net), net >= 0],
        ],
        decision: decisionFromNet(net, v.platformCost),
      };
    },
  },
  seat_allocation: {
    title: "AI Seat Allocation Calculator",
    fields: [
      ["seats", "Paid seats", 100],
      ["price", "Price / seat / month ($)", 30],
      ["activeRate", "Seats actively used (%)", 70],
    ],
    compute: (v) => {
      const total = v.seats * v.price;
      const active = v.seats * (v.activeRate / 100);
      const wasted = total * (1 - v.activeRate / 100);
      return {
        metrics: [
          ["Total spend / month", money(total)], ["Active users", `${active.toFixed(0)}`],
          ["Cost per active user", money2(active ? total / active : 0)], ["Wasted spend / month", money(wasted), wasted <= 0],
        ],
        extra: [["Wasted spend / year", money(wasted * 12)]],
      };
    },
  },
  api_cost: {
    title: "AI API Cost Calculator",
    fields: [
      ["requests", "Requests per month", 50000],
      ["inputTokens", "Input tokens per request", 1500],
      ["outputTokens", "Output tokens per request", 500],
      ["inputPrice", "Price per 1M input tokens ($)", 3],
      ["outputPrice", "Price per 1M output tokens ($)", 15],
    ],
    compute: (v) => {
      const cost = (v.requests * (v.inputTokens * v.inputPrice + v.outputTokens * v.outputPrice)) / 1_000_000;
      return {
        metrics: [
          ["Monthly API cost", money2(cost), false], ["Cost per request", money2(v.requests ? cost / v.requests : 0)],
          ["Cost per 1,000 requests", money2(v.requests ? (cost / v.requests) * 1000 : 0)], ["Annual API cost", money(cost * 12), false],
        ],
      };
    },
  },
};

const SCORECARD_QUESTIONS = [
  "We have verified time-saved data (not vendor claims) for this tool",
  "Net value is positive after licences, review and rework",
  "More than 70% of paid seats are actively used",
  "Evidence Quality is Moderate or better",
  "The price has stayed flat or fallen since we signed",
  "Teams would object if the tool was removed",
  "No cheaper verified alternative covers the same workflows",
  "Usage is trending up over the last quarter",
];

function RenewalScorecard({ onCta, ctaText }) {
  const [checked, setChecked] = useState(() => SCORECARD_QUESTIONS.map(() => false));
  const score = checked.filter(Boolean).length;
  const rec = score >= 7 ? ["Expand", "bg-green-100 text-green-700"]
    : score >= 5 ? ["Continue", "bg-emerald-50 text-emerald-700"]
    : score >= 3 ? ["Review", "bg-amber-100 text-amber-700"]
    : ["Improve or Stop", "bg-red-100 text-red-600"];
  return (
    <div className="grid gap-8 p-6 md:grid-cols-2">
      <div className="space-y-3">
        {SCORECARD_QUESTIONS.map((q, i) => (
          <label key={i} className="flex cursor-pointer items-start gap-2.5 text-sm text-slate-700">
            <Checkbox checked={checked[i]} data-testid={`scorecard-q-${i}`}
              onCheckedChange={(v) => setChecked((c) => c.map((x, j) => (j === i ? !!v : x)))} className="mt-0.5" />
            {q}
          </label>
        ))}
      </div>
      <div className="flex flex-col rounded-xl border border-slate-200 bg-background p-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Renewal recommendation</p>
        <p className="mt-4 font-mono text-4xl font-semibold text-navy" data-testid="scorecard-score">{score}<span className="text-lg text-slate-400">/8</span></p>
        <span className={`mt-3 w-fit rounded-full px-3 py-1 text-sm font-medium ${rec[1]}`} data-testid="scorecard-decision">{rec[0]}</span>
        <p className="mt-4 text-sm text-slate-500">7+ Expand · 5–6 Continue · 3–4 Review · under 3 Improve or Stop before renewal.</p>
        <Button className="mt-5 w-full bg-editorial hover:bg-editorial/90" onClick={onCta} data-testid="calc-cta">
          {ctaText} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export function CalculatorWidget({ primaryCta, type = "ai_roi" }) {
  const navigate = useNavigate();
  const cfg = CALCULATORS[type] || CALCULATORS.ai_roi;
  const [values, setValues] = useState(() =>
    Object.fromEntries((cfg.fields || []).map(([k, , d]) => [k, d])));

  const r = useMemo(() => {
    if (!cfg.compute) return null;
    const nums = Object.fromEntries(Object.entries(values).map(([k, v]) => [k, Number(v) || 0]));
    return cfg.compute(nums);
  }, [cfg, values]);

  const go = () => {
    const cta = primaryCta || { destination_url: "/signup", button_text: "Start Free Trial", tracking_event: "cta_click" };
    track(cta.tracking_event || "cta_click", { source: "calculator", calculator: type, destination: cta.destination_url });
    const url = cta.destination_url || "/signup";
    if (/^https?:\/\//.test(url)) window.location.href = url;
    else navigate(url);
  };

  const DECISION_STYLE = {
    Expand: "bg-green-100 text-green-700",
    Continue: "bg-emerald-50 text-emerald-700",
    Review: "bg-amber-100 text-amber-700",
  };

  return (
    <section className="my-10 overflow-hidden rounded-2xl border border-editorial/20 bg-white shadow-sm" data-testid="resource-calculator">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-editorial/5 px-6 py-4">
        <Calculator className="h-5 w-5 text-editorial" />
        <h2 className="font-heading text-xl font-semibold text-navy">{cfg.title || "Calculator"}</h2>
      </div>
      {type === "renewal_scorecard" ? (
        <RenewalScorecard onCta={go} ctaText={(primaryCta?.button_text) || "Start Free Trial"} />
      ) : (
        <div className="grid gap-8 p-6 md:grid-cols-2">
          <div className="space-y-4">
            {cfg.fields.map(([key, label, d]) => (
              <div key={key} className="space-y-1.5">
                <Label className="text-sm font-medium text-slate-700">{label}</Label>
                <Input type="number" min="0" value={values[key]} placeholder={String(d)}
                  onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))} data-testid={`calc-${key}`} />
              </div>
            ))}
          </div>
          <div className="flex flex-col rounded-xl border border-slate-200 bg-background p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Results (computed from your inputs)</p>
            <div className="mt-3 grid grid-cols-2 gap-4">
              {(r?.metrics || []).map(([label, value, positive], i) => (
                <Metric key={i} label={label} value={value} testid={`calc-out-${i}`}
                  accent={positive === undefined ? "text-navy" : positive ? "text-green-600" : "text-coral"} />
              ))}
            </div>
            {(r?.extra || []).map(([label, value], i) => (
              <div key={i} className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4 text-sm">
                <span className="text-slate-500">{label}</span>
                <span className="font-mono font-medium text-navy">{value}</span>
              </div>
            ))}
            {r?.decision ? (
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-slate-500">Decision</span>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${DECISION_STYLE[r.decision] || "bg-slate-100 text-slate-600"}`} data-testid="calc-out-decision">{r.decision}</span>
              </div>
            ) : null}
            <Button className="mt-5 w-full bg-editorial hover:bg-editorial/90" onClick={go} data-testid="calc-cta">
              {(primaryCta?.button_text) || "Start Free Trial"} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <p className="mt-2 text-center text-xs text-slate-400">Estimate only. Track real evidence in NetLift.</p>
          </div>
        </div>
      )}
    </section>
  );
}

function Metric({ label, value, accent = "text-navy", testid }) {
  return (
    <div>
      <p className="text-xs text-slate-400">{label}</p>
      <p className={`font-mono text-lg font-semibold ${accent}`} data-testid={testid}>{value}</p>
    </div>
  );
}
